import * as React from 'react';
import { Button } from '@ooh/ui/components/button';
import { Input } from '@ooh/ui/components/input';

interface OfferTitleInlineEditProps {
  canEdit: boolean;
  disabled?: boolean;
  inputLabel: string;
  onSave: (title: string) => Promise<void>;
  title: string;
}

export function OfferTitleInlineEdit({
  canEdit,
  disabled = false,
  inputLabel,
  onSave,
  title,
}: OfferTitleInlineEditProps) {
  const [draftTitle, setDraftTitle] = React.useState(title);
  const [isEditing, setIsEditing] = React.useState(false);
  // Prevents double-save when Enter fires onKeyDown then immediately triggers onBlur.
  const isSavingRef = React.useRef(false);

  React.useEffect(() => {
    if (!isEditing) {
      setDraftTitle(title);
    }
  }, [isEditing, title]);

  const saveTitle = React.useCallback(async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    try {
      const nextTitle = draftTitle.trim();

      if (nextTitle.length === 0 || nextTitle === title) {
        setDraftTitle(title);
        setIsEditing(false);
        return;
      }

      await onSave(nextTitle);
      setIsEditing(false);
    } finally {
      isSavingRef.current = false;
    }
  }, [draftTitle, onSave, title]);

  if (!canEdit) {
    return <h1 className="truncate text-lg font-semibold text-foreground">{title}</h1>;
  }

  if (!isEditing) {
    return (
      <Button
        className="truncate text-left text-lg font-semibold text-foreground"
        data-testid="offer-title-display"
        disabled={disabled}
        type="button"
        variant="inline"
        onClick={() => setIsEditing(true)}
      >
        {title}
      </Button>
    );
  }

  return (
    <Input
      aria-label={inputLabel}
      autoFocus
      className="h-9 max-w-xl"
      data-testid="offer-title-input"
      value={draftTitle}
      onBlur={() => void saveTitle()}
      onChange={(event) => setDraftTitle(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          void saveTitle();
        }

        if (event.key === 'Escape') {
          setDraftTitle(title);
          setIsEditing(false);
        }
      }}
    />
  );
}
